import { MAX_IMAGE_COMPRESS_TARGET } from './constants';

const MIN_QUALITY = 0.65;
const MAX_DIMENSION = 2048;
const QUALITY_STEPS = [0.95, 0.9, 0.85, 0.8, 0.75, 0.7, 0.65];
const SCALE_FACTORS = [1.0, 0.9, 0.8, 0.7, 0.6, 0.5, 0.4];

interface ImageDimensions {
  width: number;
  height: number;
}

export const compressImage = async (
  file: File,
  maxSize = MAX_IMAGE_COMPRESS_TARGET
): Promise<File> => {
  if (file.size <= maxSize) {
    return file;
  }

  try {
    const img = await loadImage(file);

    for (const scale of SCALE_FACTORS) {
      let width: number;
      let height: number;

      if (scale === 1.0) {
        width = img.width;
        height = img.height;
      } else {
        const scaled = calculateAspectRatioFit(
          Math.round(img.width * scale),
          Math.round(img.height * scale),
          MAX_DIMENSION,
          MAX_DIMENSION
        );
        width = scaled.width;
        height = scaled.height;
      }

      const canvas = drawToCanvas(img, width, height);

      for (const quality of QUALITY_STEPS) {
        const compressed = await compressAndCreateFile(canvas, quality, file);
        if (compressed.size <= maxSize) return compressed;
      }
    }

    const fallback = calculateAspectRatioFit(
      Math.round(img.width * 0.4),
      Math.round(img.height * 0.4),
      MAX_DIMENSION,
      MAX_DIMENSION
    );
    const canvas = drawToCanvas(img, fallback.width, fallback.height);
    return compressAndCreateFile(canvas, MIN_QUALITY, file);
  } catch (error) {
    throw error instanceof Error ? error : new Error('이미지 압축 실패');
  }
};

const calculateAspectRatioFit = (
  srcWidth: number,
  srcHeight: number,
  maxWidth: number,
  maxHeight: number
): ImageDimensions => {
  if (srcWidth <= maxWidth && srcHeight <= maxHeight) {
    return { width: srcWidth, height: srcHeight };
  }

  const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
  return {
    width: Math.round(srcWidth * ratio),
    height: Math.round(srcHeight * ratio),
  };
};

const loadImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('이미지 로드 실패'));
    };
    img.src = url;
  });
};

const drawToCanvas = (img: HTMLImageElement, width: number, height: number): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context 생성 실패');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
};

// canvas가 gif 인코딩을 지원하지 않고 Safari는 webp도 미지원이므로 재인코딩 출력은 jpeg로 통일
const toJpegFileName = (name: string): string => name.replace(/\.[^.]+$/, '') + '.jpg';

const compressAndCreateFile = async (
  canvas: HTMLCanvasElement,
  quality: number,
  originalFile: File
): Promise<File> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Blob 생성 실패'));
          return;
        }
        const compressedFile = new File([blob], toJpegFileName(originalFile.name), {
          type: 'image/jpeg',
          lastModified: originalFile.lastModified,
        });
        resolve(compressedFile);
      },
      'image/jpeg',
      quality
    );
  });
};
